import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useLocation, useParams } from 'react-router-dom';
import { useProject } from '../../../context/ProjectContext';
import feasibilityService from '../../../services/feasibility.service';
import documentEditorService from '../../../services/documentEditor.service';
import { generateContent } from '../../../services/AIService';

const decodeHtmlEntities = (value = '') =>
  value
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');

const stripMarkup = (value = '') => {
  if (typeof value !== 'string') return value;
  return decodeHtmlEntities(
    value
      .replace(/<style[\s\S]*?<\/style>/gi, ' ')
      .replace(/<script[\s\S]*?<\/script>/gi, ' ')
      .replace(/<\/?[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  );
};

const tryParseJson = (text) => {
  if (!text || typeof text !== 'string') return null;
  const cleaned = text
    .replace(/^```json\s*/i, '')
    .replace(/^```/i, '')
    .replace(/```$/i, '')
    .trim();
  if (!cleaned) return null;
  try {
    return JSON.parse(cleaned);
  } catch (err) {
    return null;
  }
};

const extractJsonArray = (text) => {
  if (!text || typeof text !== 'string') return null;

  const fenceMatch = text.match(/```json[\s\S]*?```/i);
  if (fenceMatch) {
    const parsed = tryParseJson(fenceMatch[0]);
    if (parsed) return parsed;
  }

  const arrayMatch = text.match(/\[[\s\S]*\]/);
  if (arrayMatch) {
    try {
      return JSON.parse(arrayMatch[0]);
    } catch (err) {
      return null;
    }
  }

  const objectMatch = text.match(/\{[\s\S]*\}/);
  if (objectMatch) {
    try {
      const parsed = JSON.parse(objectMatch[0]);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && Array.isArray(parsed.rows)) return parsed.rows;
      if (parsed && Array.isArray(parsed.sites)) return parsed.sites;
    } catch (err) {
      return null;
    }
  }
  return null;
};

const normaliseRow = (row, index) => {
  if (!row || typeof row !== 'object') return null;
  const toNumber = (value) => {
    if (value === null || value === undefined) return 0;
    const numeric = Number(
      typeof value === 'string' ? value.replace(/[^0-9.+-]/g, '') : value
    );
    return Number.isFinite(numeric) ? numeric : 0;
  };

  const yesNo = (value) => {
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'string') {
      const norm = value.trim().toLowerCase();
      if (['yes', 'y', 'true', 'available'].includes(norm)) return 'Yes';
      if (['no', 'n', 'false'].includes(norm)) return 'No';
      return stripMarkup(value);
    }
    return '';
  };

  const id = row.id || row.siteId || `site-${Date.now()}-${index}`;

  return {
    id,
    pick: Boolean(row.pick || row.selected || row.priority),
    country: stripMarkup(row.country || row.country_code || ''),
    site: stripMarkup(row.site || row.site_name || ''),
    city: stripMarkup(row.city || row.location || ''),
    principalInvestigator: stripMarkup(
      row.principalInvestigator ||
        row.principal_investigator ||
        row.pi ||
        row.lead_investigator ||
        ''
    ),
    enrollPerMonth: toNumber(row.enrollPerMonth || row.enroll_per_month || row.enrollment_rate),
    screenFailPct: toNumber(row.screenFailPct || row.screen_fail_pct || row.screen_fail_rate),
    activationMonths: toNumber(row.activationMonths || row.activation_months || row.activation_time_months),
    startupCostK: toNumber(row.startupCostK || row.startup_cost_k || row.startup_cost_usd_k || row.startup_cost),
    biomarkerOnSite: yesNo(row.biomarkerOnSite || row.biomarker_on_site || row.lab_capability),
    pastPerformancePct: toNumber(row.pastPerformancePct || row.past_performance_pct || row.historical_performance_pct),
    competingTrials: toNumber(row.competingTrials || row.competing_trials || row.active_competition),
    notes: stripMarkup(row.notes || ''),
    summary: stripMarkup(row.summary || ''),
  };
};

const buildPrompt = ({ questionnaireSnapshot, inclusionText, exclusionText }) => {
  const snapshotLines = questionnaireSnapshot
    .map((item) => `${item.label}: ${item.value}`)
    .join('\n');

  return `You are a clinical operations feasibility analyst. Using the study feasibility questionnaire answers and protocol criteria, produce a ranked table of candidate investigative sites with operational metrics.

Study context:
${snapshotLines}

Inclusion Criteria (condensed):
${inclusionText || 'Not provided'}

Exclusion Criteria (condensed):
${exclusionText || 'Not provided'}

Return ONLY JSON with this shape (no narrative, no markdown):
[
  {
    "country": "US",
    "site": "MD Anderson",
    "city": "Houston",
    "principalInvestigator": "Dr. Jane Doe",
    "enrollPerMonth": 3.2,
    "screenFailPct": 18,
    "activationMonths": 3.5,
    "startupCostK": 85,
    "biomarkerOnSite": "Yes",
    "pastPerformancePct": 95,
    "competingTrials": 2,
    "notes": "Key relationships or rationale"
  }
]

Guidance:
- Include 6–12 high-quality sites spanning must-have countries first.
- Prefer sites with demonstrated biomarker capability; mark biomarkerOnSite as "Yes" or descriptive string.
- Use realistic enrollment rates (per month) and activation timelines (months).
- Screen fail, past performance, and competing trials should be conservative estimates when data are sparse; explain assumptions in notes.
- Every site MUST include a named principal investigator (use the most credible PI, lead investigator, or site lead available; if uncertain, provide the best inferred name with context in notes—never leave blank).
- Do NOT return narrative. JSON only.`;
};

const SiteFeasibility = () => {
  const { currentProject, loadProject } = useProject();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const projectIdFromUrl = queryParams.get('projectId') || queryParams.get('project');
  const { id: projectIdFromParams } = useParams();

  const [questionnaireData, setQuestionnaireData] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveAlert, setSaveAlert] = useState(null);
  const [filterCountry, setFilterCountry] = useState('ALL');
  const [summariesLoading, setSummariesLoading] = useState(false);
  const [summariesError, setSummariesError] = useState(null);

  const effectiveProjectId = useMemo(
    () => currentProject?._id || projectIdFromUrl || projectIdFromParams || '',
    [currentProject, projectIdFromParams, projectIdFromUrl]
  );

  useEffect(() => {
    if (!currentProject && effectiveProjectId) {
      loadProject(effectiveProjectId);
    }
  }, [currentProject, effectiveProjectId, loadProject]);

  useEffect(() => {
    if (!effectiveProjectId) return;
    let isMounted = true;

    const loadQuestionnaire = async () => {
      try {
        const data = await feasibilityService.getQuestionnaire(effectiveProjectId);
        if (isMounted) {
          setQuestionnaireData(data);
        }
      } catch (fetchErr) {
        console.warn('Failed to load questionnaire for site feasibility:', fetchErr);
      }
    };

    const loadSiteReport = async () => {
      try {
        const saved = await feasibilityService.getSiteReport(effectiveProjectId);
        if (isMounted && saved?.rows) {
          setRows(saved.rows);
        }
      } catch (loadErr) {
        console.warn('Failed to load saved site feasibility report:', loadErr);
      }
    };

    loadQuestionnaire();
    loadSiteReport();

    return () => {
      isMounted = false;
    };
  }, [effectiveProjectId]);

  const collectProtocolCriteria = async () => {
    let inclusionText = '';
    let exclusionText = '';

    const collectFromSections = (sectionsInput = []) => {
      if (!Array.isArray(sectionsInput)) return;
      const inclusionSections = sectionsInput.filter((section) =>
        (section.title || '').toLowerCase().includes('inclusion')
      );
      const exclusionSections = sectionsInput.filter((section) =>
        (section.title || '').toLowerCase().includes('exclusion')
      );

      if (inclusionSections.length) {
        inclusionText = inclusionSections
          .map((section) => stripMarkup(section.output || section.context || ''))
          .filter(Boolean)
          .join('\n');
      }
      if (exclusionSections.length) {
        exclusionText = exclusionSections
          .map((section) => stripMarkup(section.output || section.context || ''))
          .filter(Boolean)
          .join('\n');
      }
    };

    try {
      if (effectiveProjectId) {
        const protocolSections = await documentEditorService.getAllSections(effectiveProjectId);
        if (Array.isArray(protocolSections) && protocolSections.length) {
          collectFromSections(
            protocolSections
              .filter(
                (section) =>
                  (section.documentType ? section.documentType === 'PROTOCOL' : true) &&
                  (section.output || section.context)
              )
              .map((section) => ({
                title: section.title,
                output: stripMarkup(section.output || ''),
                context: stripMarkup(section.context || ''),
              }))
          );
        }
      }
    } catch (err) {
      console.warn('Failed to load protocol sections for site feasibility:', err);
    }

    if (!inclusionText && questionnaireData?.inclusionCriteria) {
      inclusionText = questionnaireData.inclusionCriteria;
    }
    if (!exclusionText && questionnaireData?.exclusionCriteria) {
      exclusionText = questionnaireData.exclusionCriteria;
    }

    return { inclusionText, exclusionText };
  };

  const snapshotQuestionnaire = () => {
    if (!questionnaireData) return [];
    const items = [
      { label: 'Indication & Subtype', value: stripMarkup(questionnaireData.indicationSubtype || 'Not specified') },
      { label: 'Study Design', value: stripMarkup(questionnaireData.studyDesignOverview || 'Not specified') },
      { label: 'Target Sample Size', value: questionnaireData.targetSampleSize || 'Not specified' },
      { label: 'Enrollment Duration (months)', value: questionnaireData.enrollmentMonths || 'Not specified' },
      { label: 'Must-Include Countries', value: questionnaireData.geographicMustInclude || '—' },
      { label: 'Preferred Countries', value: questionnaireData.sponsorPreferredCountries || '—' },
      { label: 'Biomarkers', value: questionnaireData.biomarkerList || '—' },
      { label: 'Sponsor Notes', value: questionnaireData.sponsorKolNotes || '—' },
    ];
    return items;
  };

  const handleGenerate = async () => {
    if (!effectiveProjectId) {
      setError('Please select a project first.');
      return;
    }

    setLoading(true);
    setError(null);
    setSaveAlert(null);

    try {
      const { inclusionText, exclusionText } = await collectProtocolCriteria();

      const questionnaireSnapshot = snapshotQuestionnaire();
      const prompt = buildPrompt({
        questionnaireSnapshot,
        inclusionText,
        exclusionText,
      });

      const rawResponse = await generateContent(
        prompt,
        '',
        '',
        'feasibility',
        'Site Feasibility Candidates'
      );

      const parsedArray = extractJsonArray(rawResponse);
      if (!parsedArray || !Array.isArray(parsedArray) || !parsedArray.length) {
        throw new Error('AI response did not contain a valid site list.');
      }

      const normalised = parsedArray
        .map((row, idx) => normaliseRow(row, idx))
        .filter(Boolean);

      if (!normalised.length) {
        throw new Error('Parsed site list was empty after normalisation.');
      }

      setRows(normalised);
    } catch (generateErr) {
      console.error('Site feasibility generation failed:', generateErr);
      setError(generateErr?.message || 'Failed to generate candidate sites.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!effectiveProjectId) {
      setSaveAlert({ severity: 'error', message: 'Please select a project first.' });
      return;
    }
    if (!rows.length) {
      setSaveAlert({ severity: 'error', message: 'Generate candidate sites before saving.' });
      return;
    }

    setSaving(true);
    setSaveAlert(null);

    try {
      await feasibilityService.saveSiteReport(effectiveProjectId, {
        rows,
      });
      setSaveAlert({ severity: 'success', message: 'Site feasibility report saved.' });
    } catch (saveErr) {
      console.error('Failed to save site feasibility report:', saveErr);
      setSaveAlert({
        severity: 'error',
        message: saveErr?.response?.data?.message || saveErr?.message || 'Failed to save report.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePickToggle = (rowId) => {
    setRows((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, pick: !row.pick } : row))
    );
  };

  const handleFilterChange = (event) => {
    setFilterCountry(event.target.value);
  };

  const handleSummaryChange = (rowId, value) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              summary: value,
            }
          : row
      )
    );
  };

  const handleGenerateSummaries = async () => {
    if (!effectiveProjectId || !rows.length) {
      setSummariesError('Generate candidate sites first.');
      return;
    }
    setSummariesLoading(true);
    setSummariesError(null);
    try {
      const summaries = await feasibilityService.generateSiteSummaries(effectiveProjectId, rows);
      if (Array.isArray(summaries) && summaries.length > 0) {
        setRows((prev) =>
          prev.map((row, idx) => ({
            ...row,
            summary: summaries[idx] != null ? String(summaries[idx]) : row.summary || '',
          }))
        );
      }
    } catch (err) {
      setSummariesError(err?.response?.data?.message || err?.message || 'Failed to generate summaries.');
    } finally {
      setSummariesLoading(false);
    }
  };

  const countryOptions = useMemo(() => {
    const set = new Set(rows.map((row) => row.country).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (filterCountry === 'ALL') return rows;
    return rows.filter((row) => row.country === filterCountry);
  }, [filterCountry, rows]);

  const renderCell = (value, opts = {}) => {
    if (value === null || value === undefined || value === '') return '—';
    if (opts.type === 'number') {
      const numeric = Number(value);
      if (Number.isNaN(numeric)) return '—';
      return opts.digits != null ? numeric.toFixed(opts.digits) : numeric.toLocaleString();
    }
    if (opts.type === 'percent') {
      const numeric = Number(value);
      if (Number.isNaN(numeric)) return '—';
      return `${numeric.toFixed(opts.digits != null ? opts.digits : 0)}%`;
    }
    if (opts.type === 'money') {
      const numeric = Number(value);
      if (Number.isNaN(numeric)) return '—';
      return `$${numeric.toLocaleString()}`;
    }
    return value;
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }} elevation={0}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Site Feasibility
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Generate and curate candidate site tables with operational metrics, then save for downstream planning.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={loading || !questionnaireData}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {loading ? 'Generating…' : 'Generate Sites'}
          </Button>
          <Button
            variant="outlined"
            onClick={handleGenerateSummaries}
            disabled={summariesLoading || !rows.length}
            startIcon={summariesLoading ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {summariesLoading ? 'Generating…' : 'Generate Summaries'}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {saving ? 'Saving…' : 'Save Sites'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {saveAlert && (
        <Alert severity={saveAlert.severity} sx={{ mb: 2 }}>
          {saveAlert.message}
        </Alert>
      )}
      {summariesError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSummariesError(null)}>
          {summariesError}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Candidate Sites
        </Typography>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="site-filter-country">Show</InputLabel>
          <Select
            labelId="site-filter-country"
            value={filterCountry}
            label="Show"
            onChange={handleFilterChange}
          >
            <MenuItem value="ALL">All countries</MenuItem>
            {countryOptions.map((country) => (
              <MenuItem key={country} value={country}>
                {country}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          borderRadius: 2,
          border: '1px solid rgba(224,224,224,1)',
          overflowX: 'auto',
          overflowY: 'hidden',
          maxWidth: '100%',
        }}
      >
        <Table
          size="small"
          sx={{
            width: '100%',
            tableLayout: 'fixed',
            '& .MuiTableCell-root': {
              borderRight: '1px solid rgba(224,224,224,1)',
              borderBottom: '1px solid rgba(224,224,224,1)',
              padding: '10px 12px',
              lineHeight: 1.4,
              fontSize: 13,
              verticalAlign: 'middle',
              whiteSpace: 'normal',
              wordWrap: 'break-word',
            },
            '& .MuiTableHead-root .MuiTableCell-root': {
              backgroundColor: 'rgba(0,0,0,0.04)',
              fontWeight: 600,
              fontSize: 13,
              whiteSpace: 'normal',
              wordWrap: 'break-word',
              padding: '10px 12px',
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ width: '44px' }}>Pick</TableCell>
              <TableCell sx={{ width: '5%' }}>Country</TableCell>
              <TableCell sx={{ width: '6%' }}>City</TableCell>
              <TableCell sx={{ width: '9%' }}>Site</TableCell>
              <TableCell sx={{ width: '10%' }}>Principal Investigator</TableCell>
              <TableCell align="right" sx={{ width: '6%' }}>
                Enroll /mo
              </TableCell>
              <TableCell align="right" sx={{ width: '6%' }}>
                Screen fail
              </TableCell>
              <TableCell align="right" sx={{ width: '6%' }}>
                Activate (mo)
              </TableCell>
              <TableCell align="right" sx={{ width: '6%' }}>
                Start-up ($K)
              </TableCell>
              <TableCell align="center" sx={{ width: '7%' }}>
                Biomarker on-site
              </TableCell>
              <TableCell align="right" sx={{ width: '5%' }}>
                Past perf.
              </TableCell>
              <TableCell align="right" sx={{ width: '5%' }}>
                Competing
              </TableCell>
              <TableCell sx={{ width: '29%' }}>Summary</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.length ? (
              filteredRows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Checkbox
                      checked={Boolean(row.pick)}
                      onChange={() => handlePickToggle(row.id)}
                      color="primary"
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{row.country || '—'}</TableCell>
                  <TableCell>{row.city || '—'}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{row.site || '—'}</TableCell>
                  <TableCell>{row.principalInvestigator || '—'}</TableCell>
                  <TableCell align="right">{renderCell(row.enrollPerMonth, { type: 'number', digits: 1 })}</TableCell>
                  <TableCell align="right">{renderCell(row.screenFailPct, { type: 'percent', digits: 0 })}</TableCell>
                  <TableCell align="right">{renderCell(row.activationMonths, { type: 'number', digits: 1 })}</TableCell>
                  <TableCell align="right">{renderCell(row.startupCostK, { type: 'money' })}</TableCell>
                  <TableCell align="center">{row.biomarkerOnSite || '—'}</TableCell>
                  <TableCell align="right">{renderCell(row.pastPerformancePct, { type: 'percent', digits: 0 })}</TableCell>
                  <TableCell align="right">{renderCell(row.competingTrials, { type: 'number', digits: 0 })}</TableCell>
                  <TableCell sx={{ fontSize: 12, verticalAlign: 'top', minWidth: 280 }}>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      multiline
                      minRows={6}
                      value={row.summary || ''}
                      onChange={(e) => handleSummaryChange(row.id, e.target.value)}
                      placeholder="Enter or refine site summary"
                      sx={{
                        '& .MuiInputBase-root': {
                          fontSize: 12,
                          lineHeight: 1.5,
                          alignItems: 'flex-start',
                        },
                        '& .MuiInputBase-input': {
                          resize: 'vertical',
                        },
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={13} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  {loading
                    ? 'Generating candidate sites…'
                    : 'No candidate sites yet. Generate to see AI-curated options.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default SiteFeasibility;

